/**
 * useFollowStats Hook
 * Manages follow statistics for a user profile
 */
import { useState, useEffect, useCallback } from 'react';
import { socialService } from '../../services/api/socialService';
import { FollowStats } from '../../types/social.types';

interface UseFollowStatsReturn {
  stats: FollowStats | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  incrementFollowers: () => void;
  decrementFollowers: () => void;
}

export const useFollowStats = (userId: string): UseFollowStatsReturn => {
  const [stats, setStats] = useState<FollowStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await socialService.getFollowStats(userId);
      setStats(data);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to fetch follow stats')
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const incrementFollowers = useCallback(() => {
    setStats((prev) =>
      prev
        ? {
            ...prev,
            followersCount: prev.followersCount + 1,
            isFollowing: true
          }
        : null
    );
  }, []);

  const decrementFollowers = useCallback(() => {
    setStats((prev) =>
      prev
        ? {
            ...prev,
            followersCount: prev.followersCount - 1,
            isFollowing: false
          }
        : null
    );
  }, []);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats,
    incrementFollowers,
    decrementFollowers
  };
};
